import { Component } from '@theme/component';
import { fetchConfig, preloadImage, CART_JSON_SECTION_ID, cartFromSections } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// How long the button keeps its "added" state once the addition is confirmed
const ADDED_STATE_DURATION = 800;

// Longest time the button can stay in its "adding" state
const ADDING_STATE_MAX_DURATION = 15000;

const REQUIRED_SIZE_SELECTION_SELECTOR =
  'input[data-is-size-option="true"]:checked, option[data-is-size-option="true"]:checked, input[data-requires-explicit-selection="true"]:checked, option[data-requires-explicit-selection="true"]:checked';

/**
 * Returns the size picker associated with a product form.
 * On PDPs the picker is a sibling of the buy buttons within `.product-details`.
 * @param {HTMLElement} element
 * @returns {HTMLElement | undefined}
 */
const getRequiredSizePicker = (element) => getVariantPicker(element, 'variant-picker[data-require-explicit-size-selection]');

/**
 * @param {HTMLElement} element
 * @param {string} [selector]
 * @returns {HTMLElement | undefined}
 */
const getVariantPicker = (element, selector = 'variant-picker') => {
  const scope =
    element.closest('.quick-add-modal__content, .nw-product-card__actions-desktop, .product-details') || element.parentElement;

  return Array.from(scope?.querySelectorAll(selector) || []).find(
    (picker) => picker instanceof HTMLElement && picker.dataset.productId === element.dataset.productId
  );
};

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
const hasRequiredSizeSelection = (element) => {
  const picker = getRequiredSizePicker(element);

  // The product form may connect before the picker clears Shopify's default
  // variant. Do not treat that transient checked input as a customer choice.
  if (!picker || !picker.hasAttribute('data-required-size-selection-ready')) return false;

  return Boolean(picker.querySelector(REQUIRED_SIZE_SELECTION_SELECTOR));
};

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    if (productForm?.dataset.requireExplicitSizeSelection === 'true') {
      if (!hasRequiredSizeSelection(productForm)) {
        productForm.showRequiredSizeSelection();
        return;
      }
    }
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    // Clicked by the sticky bar ("puppet"): no fly-to-cart from a button that is out of view.
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
    }
    this.showAdding();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Shows the "added" state as soon as the button is pressed (an addition almost never fails), and marks the
   * addition as pending until it is confirmed or fails (see `finishAdding`). The button is not disabled: the form
   * ignores submissions from a disabled button.
   */
  showAdding() {
    const { addToCartButton } = this.refs;

    this.#clearResetTimeouts();
    addToCartButton.dataset.added = 'true';
    addToCartButton.setAttribute('aria-busy', 'true');

    // Safety net: never leave the button busy if no answer arrives.
    this.#resetTimeouts.push(setTimeout(() => this.finishAdding(false), ADDING_STATE_MAX_DURATION));
  }

  /**
   * Ends the pending addition: keeps the "added" state for a moment when it was confirmed, or goes back to the normal
   * state when it failed.
   * @param {boolean} added - Whether the product was added
   */
  finishAdding(added) {
    const { addToCartButton } = this.refs;
    if (addToCartButton.getAttribute('aria-busy') !== 'true') return;

    this.#clearResetTimeouts();
    addToCartButton.removeAttribute('aria-busy');
    if (!added) {
      addToCartButton.removeAttribute('data-added');
      // Lets whatever was shown ahead of the answer (the "added to cart" modal) undo itself.
      this.dispatchEvent(new CustomEvent(ThemeEvents.cartAddFailed, { bubbles: true }));
      return;
    }

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
      this.#resetTimeouts = this.#resetTimeouts.filter((id) => id !== timeoutId);
    }, ADDED_STATE_DURATION);
    this.#resetTimeouts.push(timeoutId);
  }

  #clearResetTimeouts() {
    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    this.#syncRequiredSizeSelection();

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const form = this.querySelector('form');
    if (form && window.nwEarlySubmits?.has(form)) {
      const submitter = window.nwEarlySubmits.get(form);
      window.nwEarlySubmits.delete(form);
      this.#replayEarlySubmit(form, submitter);
    }
  }

  /**
   * Sends an addition the shopper asked for while the page was still loading. The native submission was stopped (see
   * the early submit guard in layout/theme.liquid): it would have left the page and added the server's default variant
   * instead of the size picked meanwhile.
   * @param {HTMLFormElement} form
   * @param {HTMLElement | null} submitter
   */
  async #replayEarlySubmit(form, submitter) {
    // The variant picker keeps a size picked before it was ready (variant-picker.js): let it apply it first.
    if (getVariantPicker(this) && !customElements.get('variant-picker')) await customElements.whenDefined('variant-picker');
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve)));
    if (!this.isConnected) return;

    const pickedVariantId = this.#getPickedVariantId();
    const { variantId } = this.refs;
    if (pickedVariantId && variantId instanceof HTMLInputElement) {
      variantId.value = pickedVariantId;
      variantId.disabled = false;
    }

    const button = submitter instanceof HTMLButtonElement && submitter.form === form ? submitter : undefined;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(button);
    } else if (button) {
      // Safari before 16 has no requestSubmit.
      button.click();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  /**
   * The variant of the option checked in this product's variant picker (the size first, when there are several options).
   * @returns {string | undefined}
   */
  #getPickedVariantId() {
    const picker = getVariantPicker(this);
    const picked =
      picker?.querySelector('input[type="radio"][data-is-size-option="true"]:checked[data-variant-id]') ??
      picker?.querySelector('input[type="radio"]:checked[data-variant-id]');

    return picked instanceof HTMLInputElement ? picked.dataset.variantId || undefined : undefined;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<Cart | null>} The updated cart
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = /** @type {Cart} */ (await response.json());

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Ends the in-progress state of the add buttons of this form.
   * @param {boolean} added - Whether the product was added
   */
  #finishAdding(added) {
    for (const container of this.querySelectorAll('add-to-cart-component')) {
      /** @type {AddToCartComponent} */ (container).finishAdding?.(added);
    }
  }

  /**
   * Gets the cart after an addition: from the cart JSON section of the response when it's there, so the confirmation
   * doesn't wait for another request, and otherwise from `/cart.js`.
   * @param {Record<string, string | null> | undefined} sections - The `sections` of the cart/add response
   * @returns {Promise<Cart | null>} The updated cart
   */
  async #getUpdatedCart(sections) {
    const cart = /** @type {Cart | null} */ (cartFromSections(sections));
    if (!cart) return this.#fetchAndUpdateCartQuantity();

    this.#updateCartQuantityFromData(cart);
    return cart;
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#hasPendingRequiredSizeSelection()) {
      this.#finishAdding(false);
      this.showRequiredSizeSelection();
      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
        this.refs.addToCartButtonContainer?.showAdding?.();
        this.#announceAddStart(intendedVariantId, quantity);
      } else {
        this.#finishAdding(false);
      }
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * Announces an addition before it is sent, so the "added to cart" modal can open at once instead of after the cart
   * answers. The confirmation (`CartAddEvent`) or the failure (`cartAddFailed`) follows from this same form.
   * @param {string} variantId
   * @param {number} quantity
   */
  #announceAddStart(variantId, quantity) {
    this.dispatchEvent(
      new CustomEvent(ThemeEvents.cartAddStart, {
        bubbles: true,
        detail: { source: 'product-form-component', productId: this.dataset.productId, variantId, quantity },
      })
    );
  }

  /** Opens the size selector for a form that requires an explicit size choice. */
  showRequiredSizeSelection() {
    const quickAdd = this.closest('.nw-detail-buy-buttons')?.querySelector('quick-add-component');
    if (quickAdd?.handleClick) {
      quickAdd.handleClick(new Event('click'));
      return;
    }

    this.dispatchEvent(new CustomEvent('size-selection-required', { bubbles: true }));
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    const embeddedVariantId = this.#getSelectedEmbeddedVariantId();
    if (embeddedVariantId) return embeddedVariantId;

    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {string | undefined} */
  #getSelectedEmbeddedVariantId() {
    const actionsHost = this.closest(
      '.nw-complete-look-card__actions, .nw-product-card__actions, .nw-product-card__actions-desktop'
    );
    const embeddedPicker = actionsHost?.querySelector('variant-picker[data-nw-embedded-variant-picker]');
    const selectedOption = embeddedPicker?.querySelector(
      'input[type="radio"]:checked[data-variant-id], select option:checked[data-variant-id]'
    );

    if (selectedOption instanceof HTMLElement) {
      return selectedOption.dataset.variantId || undefined;
    }

    return undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;
    const selectedVariantId = overrideVariantId || this.#getSelectedEmbeddedVariantId();

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) {
        this.#finishAdding(false);
        return;
      }
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        this.#finishAdding(false);
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (selectedVariantId) {
      formData.set('id', selectedVariantId);
    }
    // A size picked while the page was loading can reach the picker before this form listens to it, leaving the
    // variant field cleared until the picker's update arrives: take the variant from the picker meanwhile.
    if (!formData.get('id')) {
      const pickedVariantId = this.#getPickedVariantId();
      if (pickedVariantId) formData.set('id', pickedVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    formData.set('sections', [...cartItemComponentsSectionIds, CART_JSON_SECTION_ID].join(','));

    this.#announceAddStart(
      String(formData.get('id') || ''),
      Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.#finishAdding(false);
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // The updated cart, to get the actual total quantity for this variant
          const updatedCart = await this.#getUpdatedCart(response.sections);
          this.#finishAdding(true);
          this.#publishProductAdded(id.toString(), Number(formData.get('quantity')) || Number(this.dataset.quantityDefault));

          this.dispatchEvent(
            new CartAddEvent(updatedCart || {}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              variantId: id.toString(),
              addedItem: response,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        this.#finishAdding(false);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        ...this.#linePropertiesForCurrentVariant(),
      })),
      sections: [...cartItemComponentsSectionIds, CART_JSON_SECTION_ID].join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.#finishAdding(false);
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const updatedCart = await this.#getUpdatedCart(response.sections);
        this.#finishAdding(true);

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        if (items.length === 1) {
          this.#publishProductAdded(items[0].variantId, items[0].quantity);
        }
        this.dispatchEvent(
          new CartAddEvent(updatedCart || {}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            variantId: items.length === 1 ? items[0].variantId : undefined,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        this.#finishAdding(false);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */(newElement.cloneNode(true)));
    }
  }

  /**
   * Carousel cards use `data-nw-embedded-variant-picker`; when that row shares a section with the main PDP form, the
   * bubbled variant event must only update the product-form next to the embedded picker.
   * @param {Event} event
   * @returns {boolean}
   */
  #shouldIgnoreCarouselEmbeddedVariantEvent(event) {
    const embeddedPicker = /** @type {HTMLElement | undefined} */ (
      event.composedPath().find(
        (n) =>
          n instanceof HTMLElement &&
          n.localName === 'variant-picker' &&
          n.hasAttribute('data-nw-embedded-variant-picker')
      )
    );
    if (!embeddedPicker) return false;
    const actionsHost = embeddedPicker.closest(
      '.nw-complete-look-card__actions, .nw-product-card__actions, .nw-product-card__actions-desktop'
    );
    const formForPicker = actionsHost?.querySelector('product-form-component');
    return !formForPicker || formForPicker !== this;
  }

  /**
   * Variant events bubble through the shared PDP section. Ignore selections made for another product form.
   * @param {Event} event
   * @returns {boolean}
   */
  #isVariantEventForCurrentProduct(event) {
    const variantPicker = /** @type {HTMLElement | undefined} */ (
      event.composedPath().find((node) => node instanceof HTMLElement && node.localName === 'variant-picker')
    );

    if (!variantPicker?.dataset.productId) return true;

    return String(variantPicker.dataset.productId) === String(this.dataset.productId);
  }

  #hasPendingRequiredSizeSelection() {
    if (this.dataset.requireExplicitSizeSelection !== 'true') return false;

    return !hasRequiredSizeSelection(this);
  }

  #syncRequiredSizeSelection() {
    if (!this.#hasPendingRequiredSizeSelection()) return;

    this.refs.variantId.value = '';
    this.refs.variantId.disabled = true;
  }

  #linePropertiesForCurrentVariant() {
    const input = this.querySelector('input[name="properties[_productm]"]');
    const productm = input instanceof HTMLInputElement ? input.value.trim() : '';

    return productm === '' ? {} : { properties: { _productm: productm } };
  }

  #publishProductAdded(variantId, quantity) {
    const input = this.querySelector('input[name="properties[_productm]"]');
    const parsedProductm = input instanceof HTMLInputElement ? Number(input.value) : Number.NaN;

    window.DisfrazzesAnalytics?.publish?.('product_added_to_cart', {
      variant_id: variantId,
      quantity,
      productm: Number.isFinite(parsedProductm) ? parsedProductm : null,
    });
  }

  #syncProductmProperty(nextMarkup) {
    const form = this.querySelector('form');
    if (!form) return;

    const current = form.querySelector('input[name="properties[_productm]"]');
    const next = nextMarkup.querySelector('input[name="properties[_productm]"]');

    if (next instanceof HTMLInputElement) {
      if (current instanceof HTMLInputElement) {
        current.value = next.value;
      } else {
        form.append(next.cloneNode(true));
      }
    } else if (current instanceof HTMLInputElement) {
      current.remove();
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (this.#shouldIgnoreCarouselEmbeddedVariantEvent(event)) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    const hasPendingSizeSelection = this.#hasPendingRequiredSizeSelection();
    variantId.value = hasPendingSizeSelection ? '' : event.detail.resource?.id ?? '';
    variantId.disabled = hasPendingSizeSelection || event.detail.resource == null || event.detail.resource.available == false;
    this.#syncProductmProperty(event.detail.data.html);

    const currentBuyButtonsBlock = this.closest('.buy-buttons-block');
    const currentBuyButtonsKey = Array.from(currentBuyButtonsBlock?.classList || []).find((className) =>
      className.startsWith('buy-buttons-block--')
    );
    const newBuyButtonsBlock = currentBuyButtonsKey
      ? event.detail.data.html.querySelector(`.${currentBuyButtonsKey}`)
      : event.detail.data.html.querySelector('.buy-buttons-block');

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    if (currentBuyButtonsBlock && newBuyButtonsBlock) {
      morph(currentBuyButtonsBlock, newBuyButtonsBlock);
      await this.#fetchAndUpdateCartQuantity();
      return;
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButtonContainer = event.detail.data.html.querySelector('[ref="addToCartButtonContainer"]');
    const newProductFormButtons = event.detail.data.html.querySelector('.nw-product-form-buttons, .product-form-buttons');
    const currentProductFormButtons = this.refs.productFormButtons;

    const currentCtaMode = currentAddToCartButtonContainer ? 'add-to-cart' : 'notify';
    const newCtaMode = newAddToCartButtonContainer ? 'add-to-cart' : 'notify';

    if (currentCtaMode !== newCtaMode && currentProductFormButtons && newProductFormButtons) {
      morph(currentProductFormButtons, newProductFormButtons);

      if (acceleratedCheckoutButtonContainer) {
        if (event.detail.resource == null || event.detail.resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      await this.#fetchAndUpdateCartQuantity();
      return;
    }

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (hasPendingSizeSelection || event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons, .nw-product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} event */
  #onVariantSelected = (event) => {
    if (this.#shouldIgnoreCarouselEmbeddedVariantEvent(event)) return;
    if (!this.#isVariantEventForCurrentProduct(event)) return;
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
